import React, { useState, useEffect } from "react";
import "./Analytics.scss"; // Add CSS file for role switcher styling
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  Area,
  AreaChart,
} from "recharts";
import { useAuth } from "../../contexts/AuthContext";
import axiosInstance from "../../axiosInstance";
import {
  DollarSign,
  Users,
  TrendingUp,
  Briefcase,
  Award,
  Heart,
  UserCheck,
  Clock,
  BarChart3,
  AlertCircle,
} from "lucide-react";
import AccessDenied from "../AccessDenied";

const CHART_COLORS = {
  primary: ["#3b82f6", "#60a5fa", "#93c5fd", "#bfdbfe"],
  success: ["#10b981", "#34d399", "#6ee7b7", "#a7f3d0"],
  warning: ["#f59e0b", "#fbbf24", "#fcd34d", "#fde68a"],
  purple: ["#8b5cf6", "#a78bfa", "#c4b5fd", "#ddd6fe"],
  pink: ["#ec4899", "#f472b6", "#f9a8d4", "#fbcfe8"],
  diverse: [
    "#3b82f6",
    "#8b5cf6",
    "#ec4899",
    "#f59e0b",
    "#10b981",
    "#06b6d4",
    "#ef4444",
    "#14b8a6",
  ],
};

// Enum display names
const ENUM_LABELS = {
  bloodGroup: {
    A_POSITIVE: "A+",
    A_NEGATIVE: "A-",
    B_POSITIVE: "B+",
    B_NEGATIVE: "B-",
    AB_POSITIVE: "AB+",
    AB_NEGATIVE: "AB-",
    O_POSITIVE: "O+",
    O_NEGATIVE: "O-",
  },
  employmentType: {
    PERMANENT: "Permanent",
    INTERN: "Intern",
    CONTRACT: "Contract",
    PART_TIME: "Part Time",
  },
  gender: {
    MALE: "Male",
    FEMALE: "Female",
    OTHER: "Other",
    PREFER_NOT_TO_SAY: "Prefer Not to Say",
  },
  maritalStatus: {
    SINGLE: "Single",
    MARRIED: "Married",
    DIVORCED: "Divorced",
    WIDOWED: "Widowed",
    SEPARATED: "Separated",
    OTHER: "Other",
  },
  employmentStatus: {
    PROBATION: "Probation",
    CONFIRMED: "Confirmed",
    ACTIVE: "Active",
    TERMINATED: "Terminated",
    RESIGNED: "Resigned",
  },
  employmentLevel: {
    ENTRY_LEVEL: "Entry Level",
    MID_LEVEL: "Mid Level",
    TOP_MANAGEMENT: "Top Management",
  },
  skillLevel: {
    NOVICE: "Novice",
    ADVANCED_BEGINNER: "Advanced Beginner",
    COMPETENT: "Competent",
    PROFICIENT: "Proficient",
    EXPERT: "Expert",
  },
  probationPeriod: {
    NONE: "None",
    ONE_MONTH: "1 Month",
    TWO_MONTHS: "2 Months",
    THREE_MONTHS: "3 Months",
    SIX_MONTHS: "6 Months",
    TWELVE_MONTHS: "12 Months",
  },
};

const StatsCard = ({ icon: Icon, title, value, color = "blue", subtitle }) => (
  <div className={`stats-card stats-card--${color}`}>
    <div className="stats-card__accent" aria-hidden="true" />
    <div className="stats-card__icon">
      <Icon size={20} />
    </div>
    <div className="stats-card__content">
      <h3 className="stats-card__title">{title}</h3>
      {subtitle && <p className="stats-card__subtitle">{subtitle}</p>}
    </div>
    <div className="stats-card__value">{value}</div>
  </div>
);

const ChartCard = ({ title, subtitle, children, fullWidth = false }) => (
  <div className={`chart-card ${fullWidth ? "chart-card--full" : ""}`}>
    <div className="chart-card__header">
      <h3 className="chart-card__title">{title}</h3>
      {subtitle && <p className="chart-card__subtitle">{subtitle}</p>}
    </div>
    <div className="chart-card__content">{children}</div>
  </div>
);

const LoadingSpinner = () => (
  <div className="loading-spinner">
    <div className="spinner"></div>
    <p>Loading Payroll Analytics...</p>
  </div>
);

const ErrorMessage = ({ message }) => (
  <div className="error-message">
    <AlertCircle size={16} />
    <span>{message}</span>
  </div>
);

const PayrollAnalytics = () => {
  const { getUserAuthorities } = useAuth();

  const availableRoles = getUserAuthorities();
  const allowedRoles = ["ROLE_HR", "ROLE_ADMIN", "ROLE_MANAGER"];
  const userAnalyticsRoles = availableRoles.filter((r) =>
    allowedRoles.includes(r),
  );

  const [selectedRole, setSelectedRole] = useState(userAnalyticsRoles[0] || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const hasMultipleRoles = userAnalyticsRoles.length > 1;
  const isAdmin = selectedRole === "ROLE_ADMIN";
  const isHR = selectedRole === "ROLE_HR";
  const isManager = selectedRole === "ROLE_MANAGER";

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        let endpoint = null;

        if (isAdmin) {
          endpoint = `${process.env.REACT_APP_API_BASE_URL}/analytics/employee-salary-master/report`;
        } else if (isHR) {
          endpoint = `${process.env.REACT_APP_API_HR_URL}/analytics/employee-salary-master/report`;
        } else if (isManager) {
          endpoint = `${process.env.REACT_APP_API_MANAGER_URL}/analytics/employee-salary-master/report`;
        }

        const response = await axiosInstance.get(endpoint);
        setData(response.data?.data);
        setError(null);
      } catch (err) {
        setError("Failed to load payroll analytics data");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedRole]);

  if (!userAnalyticsRoles.length) return <AccessDenied />;

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (!data) return <ErrorMessage message="No payroll data available" />;

  const { totalEmployeeCount, employeeAnalytics, employeeListSalary } = data;

  // Calculate salary statistics
  const salaries = employeeListSalary?.map((emp) => emp.actualSalary) || [];
  const totalPayroll = salaries.reduce((sum, sal) => sum + sal, 0);
  const avgSalary = salaries.length > 0 ? totalPayroll / salaries.length : 0;
  const maxSalary = salaries.length > 0 ? Math.max(...salaries) : 0;
  const minSalary = salaries.length > 0 ? Math.min(...salaries) : 0;
  const medianSalary =
    salaries.length > 0
      ? [...salaries].sort((a, b) => a - b)[Math.floor(salaries.length / 2)]
      : 0;

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Transform data for charts
  const bloodGroupData =
    employeeAnalytics.bloodGroup?.map((item) => ({
      name: ENUM_LABELS.bloodGroup[item.bloodGroupName] || item.bloodGroupName,
      value: item.totalEmployeeCount,
      fullName: item.bloodGroupName,
    })) || [];

  const employmentTypeData =
    employeeAnalytics.employmentType?.map((item) => ({
      name:
        ENUM_LABELS.employmentType[item.employmentTypeName] ||
        item.employmentTypeName,
      employees: item.totalEmployeeCount,
    })) || [];

  const genderData =
    employeeAnalytics.gender?.map((item) => ({
      name: ENUM_LABELS.gender[item.genderName] || item.genderName,
      value: item.totalEmployeeCount,
    })) || [];

  const maritalStatusData =
    employeeAnalytics.maritalStatus?.map((item) => ({
      name:
        ENUM_LABELS.maritalStatus[item.maritalStatusName] ||
        item.maritalStatusName,
      value: item.totalEmployeeCount,
    })) || [];

  const employmentStatusData =
    employeeAnalytics.employmentStatus?.map((item) => ({
      name:
        ENUM_LABELS.employmentStatus[item.employmentStatusName] ||
        item.employmentStatusName,
      employees: item.totalEmployeeCount,
    })) || [];

  const employmentLevelData =
    employeeAnalytics.employmentLevel?.map((item) => ({
      name:
        ENUM_LABELS.employmentLevel[item.employmentLevelName] ||
        item.employmentLevelName,
      employees: item.totalEmployeeCount,
    })) || [];

  const skillLevelData =
    employeeAnalytics.skillLevel?.map((item) => ({
      name: ENUM_LABELS.skillLevel[item.skillLevelName] || item.skillLevelName,
      value: item.totalEmployeeCount,
    })) || [];

  const probationPeriodData =
    employeeAnalytics.probationPeriod?.map((item) => ({
      name:
        ENUM_LABELS.probationPeriod[item.probationPeriodName] ||
        item.probationPeriodName,
      employees: item.totalEmployeeCount,
    })) || [];

  // Create salary distribution bins
  const salaryBins = [
    { range: "0-3L", min: 0, max: 300000, count: 0 },
    { range: "3-5L", min: 300000, max: 500000, count: 0 },
    { range: "5-7L", min: 500000, max: 700000, count: 0 },
    { range: "7-10L", min: 700000, max: 1000000, count: 0 },
    { range: "10L+", min: 1000000, max: Infinity, count: 0 },
  ];

  salaries.forEach((salary) => {
    const bin = salaryBins.find((b) => salary >= b.min && salary < b.max);
    if (bin) bin.count++;
  });

  // Calculate insights
  const permanentCount =
    employeeAnalytics.employmentType?.find(
      (e) => e.employmentTypeName === "PERMANENT",
    )?.totalEmployeeCount || 0;
  const permanentPercentage =
    totalEmployeeCount > 0
      ? ((permanentCount / totalEmployeeCount) * 100).toFixed(1)
      : 0;

  const confirmedCount =
    employeeAnalytics.employmentStatus?.find(
      (e) => e.employmentStatusName === "CONFIRMED",
    )?.totalEmployeeCount || 0;

  const expertCount =
    employeeAnalytics.skillLevel?.find((e) => e.skillLevelName === "EXPERT")
      ?.totalEmployeeCount || 0;
  const proficientCount =
    employeeAnalytics.skillLevel?.find((e) => e.skillLevelName === "PROFICIENT")
      ?.totalEmployeeCount || 0;
  const seniorTalent = expertCount + proficientCount;

  // Calculate salary by employment type
  const salaryByType =
    employeeAnalytics.employmentType?.map((item) => {
      const typeName = item.employmentTypeName;
      const typeEmployees = employeeListSalary.filter((emp) => {
        // This is a simplified mapping - you might need to adjust based on your actual data structure
        return true; // Add your logic here if you have employment type per employee
      });
      return {
        name: ENUM_LABELS.employmentType[typeName] || typeName,
        employees: item.totalEmployeeCount,
        avgSalary: avgSalary, // This would need proper calculation per type if data available
      };
    }) || [];

  return (
    <div className="overview-grid">
      {/* Payroll Overview Section */}
      <div className="analytics-section">
        <div className="section-header">
          <DollarSign size={28} />
          <h2>Payroll Overview</h2>
          {hasMultipleRoles && (
            <div className="role-switcher" style={{ marginLeft: "auto" }}>
              {userAnalyticsRoles.map((role) => {
                const formattedRole = role
                  .replace("ROLE_", "")
                  .toLowerCase()
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (char) => char.toUpperCase());

                return (
                  <button
                    key={role}
                    className={`role-toggle-btn ${
                      selectedRole === role ? "role-toggle-btn--active" : ""
                    }`}
                    onClick={() => setSelectedRole(role)}
                  >
                    {formattedRole}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="stats-grid stats-grid--compact">
          <StatsCard
            icon={Users}
            title="Total Employees"
            value={totalEmployeeCount}
            color="blue"
            subtitle="On payroll"
          />
          <StatsCard
            icon={DollarSign}
            title="Total Payroll"
            value={formatCurrency(totalPayroll)}
            color="green"
            subtitle="Monthly compensation"
          />
          <StatsCard
            icon={TrendingUp}
            title="Average Salary"
            value={formatCurrency(avgSalary)}
            color="purple"
            subtitle="Per employee"
          />
          <StatsCard
            icon={BarChart3}
            title="Median Salary"
            value={formatCurrency(medianSalary)}
            color="orange"
            subtitle="Middle value"
          />
        </div>

        <div className="charts-row">
          <ChartCard
            title="Salary Distribution"
            subtitle="Employees across salary bands"
          >
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={salaryBins}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="range" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip />
                <Bar
                  dataKey="count"
                  fill={CHART_COLORS.primary[0]}
                  name="Employees"
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            title="Compensation Insights"
            subtitle="Salary range and distribution"
          >
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={[
                  { metric: "Minimum", amount: minSalary },
                  { metric: "Average", amount: avgSalary },
                  { metric: "Median", amount: medianSalary },
                  { metric: "Maximum", amount: maxSalary },
                ]}
                layout="vertical"
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" stroke="#6b7280" />
                <YAxis
                  dataKey="metric"
                  type="category"
                  stroke="#6b7280"
                  width={80}
                />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Bar
                  dataKey="amount"
                  fill={CHART_COLORS.success[0]}
                  radius={[0, 8, 8, 0]}
                >
                  {[0, 1, 2, 3].map((index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={
                        CHART_COLORS.diverse[
                          index % CHART_COLORS.diverse.length
                        ]
                      }
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </div>

      {/* Employee Demographics & Payroll Section */}
      <div className="analytics-section">
        <div className="section-header">
          <Users size={28} />
          <h2>Workforce & Compensation Analysis</h2>
        </div>

        <div className="charts-row">
          <ChartCard
            title="Employment Type Distribution"
            subtitle="Breakdown by contract type"
          >
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={employmentTypeData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip />
                <Bar
                  dataKey="employees"
                  fill={CHART_COLORS.primary[0]}
                  name="Employees"
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            title="Employment Status"
            subtitle="Current employment standing"
          >
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={employmentStatusData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip />
                <Bar
                  dataKey="employees"
                  fill={CHART_COLORS.success[0]}
                  name="Employees"
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        <div className="charts-row">
          <ChartCard
            title="Gender Distribution"
            subtitle="Workforce diversity breakdown"
          >
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={genderData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value, percent }) =>
                    `${name}: ${value} (${(percent * 100).toFixed(0)}%)`
                  }
                  outerRadius={90}
                  dataKey="value"
                >
                  {genderData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={
                        CHART_COLORS.primary[
                          index % CHART_COLORS.primary.length
                        ]
                      }
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            title="Marital Status"
            subtitle="Employee marital status distribution"
          >
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={maritalStatusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value, percent }) =>
                    `${name}: ${value} (${(percent * 100).toFixed(0)}%)`
                  }
                  outerRadius={90}
                  dataKey="value"
                >
                  {maritalStatusData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={
                        CHART_COLORS.purple[index % CHART_COLORS.purple.length]
                      }
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </div>

      {/* Career Development & Skills Section */}
      <div className="analytics-section">
        <div className="section-header">
          <Award size={28} />
          <h2>Career Development & Skills</h2>
        </div>

        <div className="charts-row">
          <ChartCard
            title="Employment Level"
            subtitle="Organizational hierarchy distribution"
          >
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={employmentLevelData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" stroke="#6b7280" />
                <YAxis
                  dataKey="name"
                  type="category"
                  stroke="#6b7280"
                  width={120}
                />
                <Tooltip />
                <Bar
                  dataKey="employees"
                  fill={CHART_COLORS.warning[0]}
                  name="Employees"
                  radius={[0, 8, 8, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            title="Skill Level Distribution"
            subtitle="Technical expertise across workforce"
          >
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={skillLevelData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip />
                <Bar
                  dataKey="value"
                  fill={CHART_COLORS.purple[0]}
                  name="Employees"
                  radius={[8, 8, 0, 0]}
                >
                  {skillLevelData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={
                        CHART_COLORS.purple[index % CHART_COLORS.purple.length]
                      }
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        <ChartCard
          title="Probation Period Analysis"
          subtitle="New hire integration timeline"
          fullWidth
        >
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={probationPeriodData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="name" stroke="#6b7280" />
              <YAxis stroke="#6b7280" />
              <Tooltip />
              <Bar
                dataKey="employees"
                fill={CHART_COLORS.success[1]}
                name="Employees"
                radius={[8, 8, 0, 0]}
              >
                {probationPeriodData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={
                      CHART_COLORS.success[index % CHART_COLORS.success.length]
                    }
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Additional Demographics */}
      <div className="analytics-section">
        <div className="section-header">
          <Heart size={28} />
          <h2>Health & Personal Information</h2>
        </div>

        <ChartCard
          title="Blood Group Distribution"
          subtitle="Medical information for emergency preparedness"
          fullWidth
        >
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={bloodGroupData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="name" stroke="#6b7280" />
              <YAxis stroke="#6b7280" />
              <Tooltip />
              <Bar
                dataKey="value"
                fill={CHART_COLORS.pink[0]}
                name="Employees"
                radius={[8, 8, 0, 0]}
              >
                {bloodGroupData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={
                      CHART_COLORS.diverse[index % CHART_COLORS.diverse.length]
                    }
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Key Insights Summary */}
      <div className="analytics-section">
        <div className="section-header">
          <TrendingUp size={28} />
          <h2>Key Payroll Insights</h2>
        </div>
        <div className="stats-grid">
          <StatsCard
            icon={DollarSign}
            title="Salary Range Spread"
            value={formatCurrency(maxSalary - minSalary)}
            color="green"
            subtitle="Max-Min difference"
          />
          <StatsCard
            icon={Users}
            title="Permanent Staff"
            value={`${permanentPercentage}%`}
            color="blue"
            subtitle={`${permanentCount} permanent employees`}
          />
          <StatsCard
            icon={Award}
            title="Senior Talent Pool"
            value={seniorTalent}
            color="purple"
            subtitle="Expert & Proficient employees"
          />
          <StatsCard
            icon={UserCheck}
            title="Confirmed Employees"
            value={confirmedCount}
            color="orange"
            subtitle="Successfully confirmed staff"
          />
        </div>
      </div>
    </div>
  );
};

export default PayrollAnalytics;
